import {
  createEntityAdapter,
  createSlice,
  createAsyncThunk,
  PayloadAction
} from '@reduxjs/toolkit';
import type { Pagination } from '@justpark/api/src/types';
import transformIncludes from '@justpark/helpers/src/transformIncludes/transformIncludes';
import snakeToCamel from '@justpark/helpers/src/snakeToCamel/snakeToCamel';
import initialPaginationState from '../helpers/initialPaginationState';

const include = [
  'vehicle',
  'payment_source',
  'season_ticket',
  'season_ticket_price'
];

const transformedInclude = [...include].map(item => snakeToCamel(item));

const seasonTicketsAdapter = createEntityAdapter({});

export const getSeasonTickets = createAsyncThunk(
  'seasonTickets/getSeasonTickets',
  async (page, { rejectWithValue, extra }) => {
    try {
      const bareResponse = await extra.seasonTickets.get(page);
      const transformedData = transformIncludes(
        bareResponse,
        transformedInclude
      );

      return transformedData;
    } catch (e) {
      return rejectWithValue({
        message: e?.message || e,
        code: e?.error || 0
      });
    }
  }
);

type State = {
  loading: boolean,
  errorMessage: string,
  pagination: Pagination
};

const customInitialState: State = {
  loading: false,
  errorMessage: '',
  pagination: initialPaginationState
};

export const initialState = seasonTicketsAdapter.getInitialState(
  customInitialState
);

const seasonTicketsSlice = createSlice({
  name: 'seasonTickets',
  initialState,
  reducers: {},
  extraReducers: {
    [getSeasonTickets.pending]: (state: State): State => ({
      ...state,
      loading: true
    }),
    [getSeasonTickets.fulfilled]: (
      state: State,
      action: PayloadAction
    ): State => {
      const { data, meta } = action.payload;

      return seasonTicketsAdapter.setAll(
        {
          ...state,
          loading: false,
          pagination: meta.pagination
        },
        data
      );
    },
    [getSeasonTickets.rejected]: (
      state: State,
      action: PayloadAction
    ): State => ({
      ...state,
      loading: false,
      errorMessage: action.payload.message
    })
  }
});
export const {
  selectAll: selectSeasonTickets
} = seasonTicketsAdapter.getSelectors(state => state[seasonTicketsSlice.name]);
export const selectSeasonTicketsErrorMessage = state =>
  state[seasonTicketsSlice.name].errorMessage;
export const selectSeasonTicketsLoading = state =>
  state[seasonTicketsSlice.name].loading;
export const selectSeasonTicketsPaginationCurrentPage = state =>
  state[seasonTicketsSlice.name].pagination.currentPage;
export const selectSeasonTicketsPaginationPerPage = state =>
  state[seasonTicketsSlice.name].pagination.perPage;
export const selectSeasonTicketsPaginationTotal = state =>
  state[seasonTicketsSlice.name].pagination.total;
export const selectSeasonTicketsPaginationTotalPages = state =>
  state[seasonTicketsSlice.name].pagination.totalPages;

export default seasonTicketsSlice;
